import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { env } from '@config/env';

/**
 * "Rate this app" — routes the member to the relevant platform store's
 * review page. Driven by the `appRating*` build config:
 *   - iOS      → itms-apps deep link (`…/app/id<APPID>?action=write-review`)
 *   - Android  → Play deep link (`market://details?id=<pkg>`)
 *   - fallback → `appRatingUrl` (or a store https URL), opened in the browser
 *
 * Store deep links (itms-apps: / market:) must LEAVE the app, so they go via
 * `window.location.href` (the same mechanism the More-tab app_link tiles use);
 * https fallbacks open through the Capacitor Browser.
 */

/** iOS review deep link + https fallback for a numeric App Store id. */
function iosTargets(appId: string): { scheme: string; web: string } {
  return {
    scheme: `itms-apps://itunes.apple.com/app/id${appId}?action=write-review`,
    web: `https://apps.apple.com/app/id${appId}?action=write-review`,
  };
}

/** Android review deep link + https fallback for a Play package. */
function androidTargets(pkg: string): { scheme: string; web: string } {
  return {
    scheme: `market://details?id=${pkg}`,
    web: `https://play.google.com/store/apps/details?id=${pkg}`,
  };
}

/** Resolve the best {scheme?, web} pair for the current platform, or null. */
function resolveTarget(): { scheme?: string; web: string } | null {
  const { appRatingEnabled, appRatingIosAppId, appRatingAndroidPackageId, appRatingUrl } = env;
  if (!appRatingEnabled) return null;

  const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

  // On native we only route to the CURRENT platform's store (never send an iOS
  // user to Play, or vice-versa). An explicit `appRatingUrl` is an acceptable
  // per-platform fallback; otherwise there's no valid target → hide the entry.
  if (platform === 'ios') {
    if (appRatingIosAppId) return iosTargets(appRatingIosAppId);
    return appRatingUrl ? { web: appRatingUrl } : null;
  }
  if (platform === 'android') {
    if (appRatingAndroidPackageId) return androidTargets(appRatingAndroidPackageId);
    return appRatingUrl ? { web: appRatingUrl } : null;
  }

  // Web: an explicit URL wins, else link to whichever store listing exists.
  if (appRatingUrl) return { web: appRatingUrl };
  if (appRatingIosAppId) return { web: iosTargets(appRatingIosAppId).web };
  if (appRatingAndroidPackageId) return { web: androidTargets(appRatingAndroidPackageId).web };
  return null;
}

/** Whether a "Rate this app" entry should be shown for this build/platform. */
export function canRateApp(): boolean {
  return resolveTarget() !== null;
}

/** Open the store review page for the current platform. Best-effort. */
export async function openAppRating(): Promise<void> {
  const target = resolveTarget();
  if (!target) return;
  try {
    if (target.scheme && Capacitor.isNativePlatform()) {
      // Custom scheme → hand off to the OS (opens the App Store / Play app).
      window.location.href = target.scheme;
      return;
    }
    await Browser.open({ url: target.web });
  } catch (error) {
    console.warn('[rate-app] failed to open store:', error);
    // Last-ditch: a plain navigation to the https listing.
    try {
      window.open(target.web, '_blank', 'noopener,noreferrer');
    } catch {
      /* noop */
    }
  }
}
